// Search utility functions for text matching and highlighting
/**
 * Normalize text for search comparison
 * - Converts to lowercase
 * - Trims whitespace
 * - Normalizes multiple spaces to single space
 * - Preserves Hebrew and other Unicode characters
 */
export function normalizeText(text) {
    return text
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ');
}
/**
 * Split query into individual words
 */
export function getQueryWords(query) {
    return normalizeText(query)
        .split(' ')
        .filter(word => word.length > 0);
}
/**
 * Check if text contains exact phrase
 */
export function containsExactPhrase(text, phrase) {
    const normalizedText = normalizeText(text);
    const normalizedPhrase = normalizeText(phrase);
    return normalizedText.includes(normalizedPhrase);
}
/**
 * Check if text contains all words from query (in any order)
 */
export function containsAllWords(text, words) {
    const normalizedText = normalizeText(text);
    return words.every(word => normalizedText.includes(word));
}
/**
 * Count how many words appear in order (prefers full word matches)
 * Example: query "design sprint" in "Design Sprint Review" → 2 ordered words
 */
export function countOrderedWords(text, words) {
    const normalizedText = normalizeText(text);
    let count = 0;
    let lastIndex = -1;
    for (const word of words) {
        // First try full word match
        const wordBoundaryRegex = new RegExp(`\\b${escapeRegex(word)}\\b`, 'i');
        const match = normalizedText.substring(lastIndex + 1).match(wordBoundaryRegex);
        if (match && match.index !== undefined) {
            const absoluteIndex = lastIndex + 1 + match.index;
            if (absoluteIndex > lastIndex) {
                count++;
                lastIndex = absoluteIndex;
                continue;
            }
        }
        // Fallback to partial match (substring)
        const index = normalizedText.indexOf(word, lastIndex + 1);
        if (index > lastIndex) {
            count++;
            lastIndex = index;
        }
        else {
            break;
        }
    }
    return count;
}
/**
 * Count individual FULL word matches (word boundary matching)
 * Example: "meet" matches "meet" but NOT "meeting"
 */
export function countWordMatches(text, words) {
    const normalizedText = normalizeText(text);
    let count = 0;
    for (const word of words) {
        // Use word boundaries to match complete words only
        const wordBoundaryRegex = new RegExp(`\\b${escapeRegex(word)}\\b`, 'i');
        if (wordBoundaryRegex.test(normalizedText)) {
            count++;
        }
    }
    return count;
}
/**
 * Count partial word matches (substring matching)
 * Example: "meet" matches "meeting"
 */
export function countPartialMatches(text, words) {
    const normalizedText = normalizeText(text);
    let count = 0;
    for (const word of words) {
        // Check if any word in text starts with this query word
        const textWords = normalizedText.split(' ');
        if (textWords.some(textWord => textWord.startsWith(word) && textWord !== word)) {
            count++;
        }
    }
    return count;
}
/**
 * Calculate recency bonus based on creation date
 * - Last 24 hours: +10 points
 * - Last 7 days: +5 points
 * - Last 30 days: +2 points
 */
export function calculateRecencyBonus(createdAt) {
    if (!createdAt)
        return 0;
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now.getTime() - created.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffHours / 24;
    if (diffHours <= 24)
        return 10;
    if (diffDays <= 7)
        return 5;
    if (diffDays <= 30)
        return 2;
    return 0;
}
/**
 * Wrap matched terms in text with <mark> tags for highlighting
 * Returns HTML string with highlighted matches
 * - Full word matches: <mark class="full-match">
 * - Partial word matches: <mark class="partial-match">
 */
export function highlightMatches(text, query) {
    if (!text || !query)
        return text;
    const words = getQueryWords(query);
    let result = text;
    // Sort words by length (longest first) to avoid partial replacements
    const sortedWords = [...words].sort((a, b) => b.length - a.length);
    for (const word of sortedWords) {
        // First, try to match whole words (word boundaries)
        const wholeWordRegex = new RegExp(`\\b(${escapeRegex(word)})\\b`, 'gi');
        const hasWholeWordMatch = wholeWordRegex.test(text);
        if (hasWholeWordMatch) {
            // Full word match - use yellow/brown highlighting
            result = result.replace(wholeWordRegex, '<mark class="full-match">$1</mark>');
        }
        else {
            // Partial word match - use lighter highlighting
            const partialRegex = new RegExp(`(${escapeRegex(word)})`, 'gi');
            result = result.replace(partialRegex, '<mark class="partial-match">$1</mark>');
        }
    }
    return result;
}
/**
 * Escape special regex characters
 */
function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/**
 * Calculate total score for a single field (project/category/session name)
 *
 * Scoring Priority (highest to lowest):
 * 1. Exact phrase match: +100
 * 2. All words present (full): +50
 * 3. Word order match: +20 per ordered word
 * 4. Individual FULL word match: +30 per word (PRIORITIZED!)
 * 5. Partial word match: +3 per partial (REDUCED)
 * 6. Location bonus: +15 (session), +10 (category), +5 (project)
 */
export function calculateFieldScore(fieldValue, query, locationBonus) {
    if (!fieldValue || !query)
        return 0;
    const words = getQueryWords(query);
    let score = 0;
    // 1. Exact phrase match: +100
    if (containsExactPhrase(fieldValue, query)) {
        score += 100;
    }
    // 2. All words present: +50
    if (containsAllWords(fieldValue, words)) {
        score += 50;
    }
    // 3. Word order match: +20 per ordered word
    const orderedWords = countOrderedWords(fieldValue, words);
    score += orderedWords * 20;
    // 4. Individual FULL word match: +30 per word (INCREASED from +10)
    const wordMatches = countWordMatches(fieldValue, words);
    score += wordMatches * 30;
    // 5. Partial word match: +3 per partial (REDUCED from +5)
    const partialMatches = countPartialMatches(fieldValue, words);
    score += partialMatches * 3;
    // 6. Location bonus (session: +15, category: +10, project: +5)
    if (score > 0) {
        score += locationBonus;
    }
    return score;
}
